/**
 * Persisted "defaults" for the Happening Now carousel composer.
 *
 * The composer's defaults bar seeds every new card (platforms + expiry). We
 * persist the operator's choices to localStorage so the next session starts
 * from the same place. Expiry is stored as an absolute `datetime-local` string
 * but re-seeded to "now + N days" whenever the stored value is missing or has
 * already passed, so a stale value never sticks.
 */
import { Constants } from "src/constant";

export type ComposerDefaults = {
  platformAndroid: boolean;
  platformIos: boolean;
  /** `<input type="datetime-local">` value (local time, no timezone). */
  expiresAtLocal: string;
};

const STORAGE_KEY = "happeningNow.composerDefaults";

/** Format a Date as a `datetime-local` value (`YYYY-MM-DDTHH:mm`) in local time. */
export function toDatetimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** A `datetime-local` value `days` from `nowMs`. */
export function expiryLocalFromDays(
  days: number,
  nowMs: number = Date.now(),
): string {
  return toDatetimeLocalValue(new Date(nowMs + days * 24 * 60 * 60 * 1000));
}

/** True when a `datetime-local` string is absent or already in the past. */
function isExpiredOrEmpty(local: string, nowMs: number): boolean {
  if (!local) return true;
  const ms = new Date(local).getTime();
  return Number.isNaN(ms) || ms <= nowMs;
}

export function defaultComposerDefaults(
  nowMs: number = Date.now(),
): ComposerDefaults {
  return {
    platformAndroid: true,
    platformIos: true,
    expiresAtLocal: expiryLocalFromDays(
      Constants.HAPPENING_NOW_DEFAULT_EXPIRY_DAYS,
      nowMs,
    ),
  };
}

export function loadComposerDefaults(
  nowMs: number = Date.now(),
): ComposerDefaults {
  const fallback = defaultComposerDefaults(nowMs);
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<ComposerDefaults>;
    const platformAndroid =
      typeof parsed.platformAndroid === "boolean"
        ? parsed.platformAndroid
        : true;
    const platformIos =
      typeof parsed.platformIos === "boolean" ? parsed.platformIos : true;
    // Never persist a card with no platforms; fall back to both.
    const bothOff = !platformAndroid && !platformIos;
    const expiresAtLocal =
      typeof parsed.expiresAtLocal === "string" &&
      !isExpiredOrEmpty(parsed.expiresAtLocal, nowMs)
        ? parsed.expiresAtLocal
        : fallback.expiresAtLocal;
    return {
      platformAndroid: bothOff ? true : platformAndroid,
      platformIos: bothOff ? true : platformIos,
      expiresAtLocal,
    };
  } catch {
    return fallback;
  }
}

export function saveComposerDefaults(defaults: ComposerDefaults): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
  } catch {
    /* ignore quota / private-mode failures */
  }
}
